import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import _JSXStyle from "styled-jsx/style";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import classnames from "classnames";
import { StoryCard } from "./components/story-card";
import DefaultProps from "./default-props";
import { customerStoriesComponent } from "./customer-stories-component.scss";
import ArrowLeftSvg from "./svgs/arrow-left-svg";
import ArrowRightSvg from "./svgs/arrow-right-svg";
import SignupFormComponent from "components/core/signups/base-signup-form/signup-form-component";
const GAP = 15;
// Desktop card width — only used as the SSR/initial-render fallback for
// stepSize before useLayoutEffect measures the actual slide width. Mobile
// slides are calc(100% - 24px), so this constant must be replaced post-mount
// or slides 6–7 freeze (carousel maxTranslate stops short of the last slide).
const CARD_WIDTH = 567;
const END_MARGIN = 120;
const CustomerStoriesComponent = ({ title, theme = "light", withGetStarted = true, ctaText, ctaHref, stories = [] })=>{
    const [currentIndex, setCurrentIndex] = useState(0);
    const [maxTranslate, setMaxTranslate] = useState(0);
    const [stepSize, setStepSize] = useState(CARD_WIDTH + GAP);
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const total = stories.length;
    useLayoutEffect(()=>{
        const measure = ()=>{
            if (!viewportRef.current || !trackRef.current) return;
            const viewport = viewportRef.current;
            const cs = window.getComputedStyle(viewport);
            const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
            const contentWidth = viewport.clientWidth - padX;
            const trackWidth = trackRef.current.scrollWidth;
            setMaxTranslate(Math.max(0, trackWidth - contentWidth + END_MARGIN));
            const firstSlide = trackRef.current.children[0];
            if (firstSlide) setStepSize(firstSlide.offsetWidth + GAP);
        };
        measure();
        if (typeof window === "undefined") return undefined;
        window.addEventListener("resize", measure);
        return ()=>window.removeEventListener("resize", measure);
    }, [
        total
    ]);
    const desiredTranslate = currentIndex * stepSize;
    const actualTranslate = Math.min(desiredTranslate, maxTranslate);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex >= total - 1;
    const goPrev = useCallback(()=>{
        setCurrentIndex((i)=>Math.max(0, i - 1));
    }, []);
    const goNext = useCallback(()=>{
        setCurrentIndex((i)=>Math.min(total - 1, i + 1));
    }, [
        total
    ]);
    // Touch swipe must use native listeners (not React's onTouchMove) because
    // React attaches synthetic touch events as passive — preventDefault() inside
    // them is a silent no-op, so the page would still scroll vertically during
    // a clearly horizontal swipe. addEventListener('touchmove', ..., {passive: false})
    // is the only way to actually suppress vertical scroll once the gesture is
    // locked horizontal.
    const SWIPE_THRESHOLD = 40;
    const touchStateRef = useRef({
        startX: 0,
        startY: 0,
        active: false,
        decided: false,
        horizontal: false
    });
    const goPrevRef = useRef(goPrev);
    const goNextRef = useRef(goNext);
    goPrevRef.current = goPrev;
    goNextRef.current = goNext;
    useEffect(()=>{
        const el = viewportRef.current;
        if (!el) return undefined;
        const onStart = (e)=>{
            const t = e.touches[0];
            touchStateRef.current = {
                startX: t.clientX,
                startY: t.clientY,
                active: true,
                decided: false,
                horizontal: false
            };
        };
        const onMove = (e)=>{
            const state = touchStateRef.current;
            if (!state.active) return;
            const t = e.touches[0];
            const dx = t.clientX - state.startX;
            const dy = t.clientY - state.startY;
            if (!state.decided) {
                if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                    state.decided = true;
                    state.horizontal = Math.abs(dx) > Math.abs(dy);
                }
            }
            if (state.horizontal && e.cancelable) e.preventDefault();
        };
        const onEnd = (e)=>{
            const state = touchStateRef.current;
            if (!state.active) return;
            state.active = false;
            if (!state.horizontal) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - state.startX;
            if (dx <= -SWIPE_THRESHOLD) goNextRef.current();
            else if (dx >= SWIPE_THRESHOLD) goPrevRef.current();
        };
        el.addEventListener("touchstart", onStart, {
            passive: true
        });
        el.addEventListener("touchmove", onMove, {
            passive: false
        });
        el.addEventListener("touchend", onEnd, {
            passive: true
        });
        el.addEventListener("touchcancel", onEnd, {
            passive: true
        });
        return ()=>{
            el.removeEventListener("touchstart", onStart);
            el.removeEventListener("touchmove", onMove);
            el.removeEventListener("touchend", onEnd);
            el.removeEventListener("touchcancel", onEnd);
        };
    }, []);
    if (!total) return null;
    const isDark = theme === "dark";
    return /*#__PURE__*/ _jsxs("section", {
        className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("customer-stories", {
            "is-dark": isDark
        }) || ""),
        children: [
            (title || withGetStarted && ctaText || !withGetStarted) && /*#__PURE__*/ _jsxs("div", {
                className: `jsx-${customerStoriesComponent.__hash}` + " " + "customer-stories-header",
                children: [
                    title && /*#__PURE__*/ _jsx("h2", {
                        dangerouslySetInnerHTML: {
                            __html: title
                        },
                        className: `jsx-${customerStoriesComponent.__hash}` + " " + "customer-stories-title"
                    }),
                    withGetStarted ? ctaText && /*#__PURE__*/ _jsx("div", {
                        className: `jsx-${customerStoriesComponent.__hash}` + " " + "customer-stories-cta-wrapper",
                        children: /*#__PURE__*/ _jsx(SignupFormComponent, {
                            ctaText: ctaText,
                            useCtaText: true,
                            extraData: {
                                cta_name: "customer_stories"
                            },
                            buttonClassName: "customer-stories-cta"
                        })
                    }) : /* NOTE: this markup is intentionally duplicated in .carousel-controls below
                 rather than extracted into a helper. styled-jsx's babel plugin only adds its
                 scoping class to JSX lexically inside this component's returned tree, so JSX
                 returned from a helper function silently loses every .arrow style. */ !withGetStarted && /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-arrows",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "Previous story",
                                onClick: goPrev,
                                disabled: isFirst,
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("arrow", {
                                    "is-disabled": isFirst
                                }) || ""),
                                children: /*#__PURE__*/ _jsx(ArrowLeftSvg, {})
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "Next story",
                                onClick: goNext,
                                disabled: isLast,
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("arrow", {
                                    "is-disabled": isLast
                                }) || ""),
                                children: /*#__PURE__*/ _jsx(ArrowRightSvg, {})
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: viewportRef,
                className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-viewport",
                children: /*#__PURE__*/ _jsx("div", {
                    ref: trackRef,
                    style: {
                        transform: `translateX(-${actualTranslate}px)`
                    },
                    className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-track",
                    children: stories.map((story, idx)=>/*#__PURE__*/ _jsx("div", {
                            className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-slide",
                            children: /*#__PURE__*/ _jsx(StoryCard, {
                                story: story
                            })
                        }, story.id || idx))
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-controls",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-controls-left",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + "dots",
                                children: stories.map((_, idx)=>/*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        "aria-label": `Go to story ${idx + 1}`,
                                        "aria-current": idx === currentIndex,
                                        onClick: ()=>setCurrentIndex(idx),
                                        className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("dot", {
                                            "is-active": idx === currentIndex
                                        }) || "")
                                    }, idx))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "aria-live": "polite",
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + "counter",
                                children: [
                                    currentIndex + 1,
                                    " of ",
                                    total
                                ]
                            })
                        ]
                    }),
                    withGetStarted && /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${customerStoriesComponent.__hash}` + " " + "carousel-arrows",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "Previous story",
                                onClick: goPrev,
                                disabled: isFirst,
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("arrow", {
                                    "is-disabled": isFirst
                                }) || ""),
                                children: /*#__PURE__*/ _jsx(ArrowLeftSvg, {})
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "Next story",
                                onClick: goNext,
                                disabled: isLast,
                                className: `jsx-${customerStoriesComponent.__hash}` + " " + (classnames("arrow", {
                                    "is-disabled": isLast
                                }) || ""),
                                children: /*#__PURE__*/ _jsx(ArrowRightSvg, {})
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(_JSXStyle, {
                id: customerStoriesComponent.__hash,
                children: customerStoriesComponent
            })
        ]
    });
};
CustomerStoriesComponent.defaultProps = DefaultProps;
export default CustomerStoriesComponent;
